import React from "react";
import {
  Box, Grid, Card, CardContent, Typography,
  LinearProgress, Chip, Stack, Divider,
  useTheme, useMediaQuery
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  DirectionsCar,
  TwoWheeler,
  LocalShipping,
  People,
  EventBusy,
  Schedule,
  CheckCircle,
  PieChart,
  AirportShuttle, CommuteOutlined, DescriptionOutlined,
  Gavel, WbSunny, LocalFireDepartment,
  SwapHoriz, DirectionsBus
} from "@mui/icons-material";
import { motion } from "framer-motion";
import formatNumber from "../utils/formatNumber";

const StatCard = ({ title, value, icon, color, trend, subtitle, onClick }) => {
  return (
    <Card
      component={motion.div}
      whileHover={{ y: -5, scale: 1.02 }}
      onClick={onClick}
      sx={{
        height: "100%",
        borderRadius: 2,

        p: 1,
        mb: 4,
        background: "#111111",
        border: `1px solid rgba(253, 184, 19, 0.1)`,
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
        cursor: "pointer",
        "&:hover": {
          boxShadow: `
            0 10px 25px rgba(0, 0, 0, 0.1),
            0 6px 15px ${color}20,
            inset 0 0 0 1px rgba(255, 255, 255, 0.9),
            inset 0 0 30px ${color}08
          `,
          "&::before": {
            background: `linear-gradient(90deg, ${color} 0%, ${color}CC 100%)`,
          },
          "& .icon-container": {
            background: `linear-gradient(135deg, ${color}40 0%, ${color}25 100%)`,
            boxShadow: `0 4px 12px ${color}30`,
            transform: "scale(1.05)",
          }
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: `linear-gradient(90deg, ${color} 0%, ${color}99 100%)`,
          borderRadius: "8px 8px 0 0",
          transition: "background 0.3s ease"
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          background: `radial-gradient(circle at 20% 80%, ${color}08 0%, transparent 70%)`,
          opacity: 0.6,
          zIndex: 0,
        }
      }}
    >
      <CardContent
        sx={{
          p: 2.5,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          zIndex: 1,
          background: "transparent",
        }}
      >
        {/* HEADER ROW - Title and Icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: "rgba(255, 255, 255, 0.7)",
              textTransform: "uppercase",
              letterSpacing: "1px",
              fontFamily: "'Outfit', sans-serif",
              fontSize: "0.75rem", margin: "0px 20px 0px 0px"
            }}
          >
            {title}
          </Typography>

          <Box
            className="icon-container"
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: `linear-gradient(135deg, ${color}30 0%, ${color}15 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              border: `1px solid ${color}30`,
              boxShadow: `0 2px 8px ${color}20`,
              transition: "all 0.3s ease",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: `linear-gradient(135deg, transparent 30%, ${color}10 100%)`,
                borderRadius: "inherit",
              }
            }}
          >
            {icon}
          </Box>
        </Box>

        {/* MAIN VALUE - Centered and prominent */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 2,
            position: "relative"
          }}
        >
          <Typography
            sx={{
              fontWeight: 900,
              color: color,
              fontSize: {
                xs: String(value).length <= 3 ? "3.5rem" : String(value).length <= 5 ? "2.8rem" : "2.2rem",
                sm: String(value).length <= 3 ? "4rem" : String(value).length <= 5 ? "3.2rem" : "2.5rem",
                md: String(value).length <= 3 ? "4.5rem" : String(value).length <= 5 ? "3.5rem" : "2.8rem"
              },
              lineHeight: 1,
              textShadow: `0 4px 8px ${color}25`,
              position: "relative",
              fontFamily: "Poppins, sans-serif",
              zIndex: 1,
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: "-5px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "60%",
                height: "4px",
                background: `linear-gradient(90deg, transparent, ${color}30, transparent)`,
                borderRadius: "2px",
                opacity: 0.7
              }
            }}
          >
            {value}
          </Typography>
        </Box>

        {/* FOOTER - Trend and Subtitle */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: "auto",
            pt: 2,
            borderTop: `1px solid ${color}15`,
            background: `linear-gradient(to top, ${color}05, transparent 50%)`,
            borderRadius: "0 0 8px 8px",
            marginLeft: -3,
            marginRight: -3,
            marginBottom: -3,
            paddingLeft: 7,
            paddingRight: 3,
            paddingBottom: 1
          }}
        >
          {trend !== undefined && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                padding: "4px 8px",
                borderRadius: "6px",
                background: trend > 0 ? "rgba(76, 175, 80, 0.1)" : "rgba(244, 67, 54, 0.1)",
                border: `1px solid ${trend > 0 ? "rgba(76, 175, 80, 0.2)" : "rgba(244, 67, 54, 0.2)"}`
              }}
            >
              {trend > 0 ? (
                <TrendingUp sx={{ fontSize: 16, color: "#4caf50", fontFamily: "Poppins, sans-serif" }} />
              ) : (
                <TrendingDown sx={{ fontSize: 16, color: "#f44336", fontFamily: "Poppins, sans-serif" }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: trend > 0 ? "#4caf50" : "#f44336",
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                {trend > 0 ? "+" : ""}{trend}%
              </Typography>
            </Box>
          )}

          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontFamily: "'Outfit', sans-serif",
                fontSize: "0.75rem",
                textAlign: "center",
                padding: "4px 12px",
                borderRadius: "4px",
                color: "rgba(255, 255, 255, 0.5)",
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.1)"
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const VehicleTypeCard = ({ type, count, percentage, color, icon }) => {
  return (
    <Card
      component={motion.div}
      whileHover={{ scale: 1.03 }}
      sx={{
        borderRadius: 1,
        background: "rgba(255, 255, 255, 0.03)",
        border: `1px solid rgba(253, 184, 19, 0.1)`,
        p: 2, ml: 3.5, pt: 4
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, p: 2 }}>
        <Box
          sx={{
            width: 60,
            height: 68,
            borderRadius: "12px",
            background: `linear-gradient(135deg, ${color} 0%, ${color}80 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white"
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, pl: 2 }}>
          <Typography variant="h5" fontWeight={900} color={color} fontFamily={"'Outfit', sans-serif"}>
            {formatNumber(count)}
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.6)" }} fontWeight={500} fontFamily={"'Outfit', sans-serif"}>
            {type}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Distribution
          </Typography>
          <Typography variant="caption" fontWeight={600} color={color} fontFamily={"Poppins, sans-serif"}>
            {percentage}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: `${color}20`,
            "& .MuiLinearProgress-bar": {
              backgroundColor: color,
              borderRadius: 4
            }
          }}
        />
      </Box>
    </Card>
  );
};

const StatusProgressCard = ({ status, count, total, color }) => {
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: color
            }}
          />
          <Typography variant="body2" fontWeight={600} sx={{ color: "#ffffff" }} fontFamily={"'Outfit', sans-serif"}>
            {status}
          </Typography>
        </Box>
        <Typography variant="body2" fontWeight={700} sx={{ color: "#ffffff" }} fontFamily={"'Outfit', sans-serif"}>
          {count} <span style={{ color: "rgba(255, 255, 255, 0.5)", fontWeight: 500 }}>({percentage}%)</span>
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 10,
          borderRadius: 5,
          backgroundColor: `${color}20`,
          "& .MuiLinearProgress-bar": {
            backgroundColor: color,
            borderRadius: 5
          }
        }}
      />
    </Box>
  );
};

export default function Statistics({ setActiveSection, setStatusFilter, dashboardData = {} }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Main Statistics from backend
  const totalUsers = dashboardData?.totalCustomer || 0;
  const expiredUsers = dashboardData?.expiredCnt || 0;
  const expiringSoonUsers = dashboardData?.expireSoonCnt || 0;
  const activeUsers = dashboardData?.activePolicyCnt || 0;

  // Vehicle Statistics from backend
  const totalVehicles = dashboardData?.totalVehicle || 0;

  // Build vehicle type data from backend (fallback to 0)
  const vehicleTypeData = [
    { type: "Two Wheeler", count: dashboardData?.totalTwoWheeler || 0, color: "#ff9800", icon: <TwoWheeler /> },
    { type: "Private Car", count: dashboardData?.totalPrivateCar || 0, color: "#667eea", icon: <DirectionsCar /> },
    { type: "Passengers Vehicle", count: dashboardData?.totalPassengersVehicle || 0, color: "#26c6da", icon: <AirportShuttle /> },
    { type: "Goods Vehicle", count: dashboardData?.totalGoodsVehicle || 0, color: "#f44336", icon: <LocalShipping /> },
    { type: "Miscellaneous", count: dashboardData?.totalMiscVehicle || 0, color: "#ab47bc", icon: <CommuteOutlined /> },
    { type: "Non - Motor", count: dashboardData?.totalNonMotor || 0, color: "#78909c", icon: <DescriptionOutlined /> },
    { type: "IDV Increase", count: dashboardData?.totalIdvIncrease || 0, color: "#4caf50", icon: <TrendingUp /> },
    { type: "HP ENDORSEMENT", count: dashboardData?.totalHpEndorsement || 0, color: "#5c6bc0", icon: <Gavel /> },
    { type: "SOLAR POLICY", count: dashboardData?.totalSolarPolicy || 0, color: "#fdd835", icon: <WbSunny /> },
    { type: "Fire Policy", count: dashboardData?.totalFirePolicy || 0, color: "#ef5350", icon: <LocalFireDepartment /> },
    { type: "Name Transfer", count: dashboardData?.totalNameTransfer || 0, color: "#26a69a", icon: <SwapHoriz /> },
    { type: "SCHOOL BUS", count: dashboardData?.totalSchoolBus || 0, color: "#ffa726", icon: <DirectionsBus /> },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <Box sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <Box sx={{ mb: 2, mt: 3 }}>
          <Typography
            variant="h4"
            fontWeight={800}
            fontFamily={"Poppins, sans-serif"}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",

            }}
          >
            📊 Insurance Statistics
          </Typography>
          <Typography variant="body1" component="div" color="text.secondary" m={2} ml={7}>
            Real-time insights into your insurance portfolio
          </Typography>
        </Box>

        {/* Main Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <motion.div variants={itemVariants} whileHover={{ scale: 1.02 }}>
              <StatCard
                title="Total Customers"
                value={formatNumber(totalUsers)}
                icon={<People sx={{ color: "#667eea", fontSize: 28, fontFamily: "Poppins, sans-serif" }} />}
                color="#667eea"
                subtitle="All registered users"
                onClick={() => {
                  setStatusFilter("all");
                  setActiveSection("LeadModule");
                }}
                sx={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `
              0 10px 20px rgba(102, 126, 234, 0.15),
              inset 0 0 0 1px rgba(102, 126, 234, 0.1),
              inset 0 0 20px rgba(102, 126, 234, 0.05)
            `,
                    "& .stat-card-content": {
                      background: "linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 249, 255, 0.98))",
                    }
                  }
                }}
              />
            </motion.div>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <motion.div variants={itemVariants} whileHover={{ scale: 1.02 }}>
              <StatCard
                title="Active Policies"
                value={formatNumber(activeUsers)}
                icon={<CheckCircle sx={{ color: "#4caf50", fontSize: 28 }} />}
                color="#4caf50"
                subtitle="Valid insurance"
                onClick={() => {
                  setStatusFilter("active");
                  setActiveSection("LeadModule");
                }}
                sx={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `
              0 10px 20px rgba(76, 175, 80, 0.15),
              inset 0 0 0 1px rgba(76, 175, 80, 0.1),
              inset 0 0 20px rgba(76, 175, 80, 0.05)
            `,
                    "& .stat-card-content": {
                      background: "linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(249, 255, 249, 0.98))",
                    }
                  }
                }}
              />
            </motion.div>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <motion.div variants={itemVariants} whileHover={{ scale: 1.02 }}>
              <StatCard
                title="Expiring Soon"
                value={formatNumber(expiringSoonUsers)}
                icon={<Schedule sx={{ color: "#ff9800", fontSize: 28 }} />}
                color="#ff9800"
                subtitle="Within 30 days"
                onClick={() => {
                  setStatusFilter("expiring soon");
                  setActiveSection("LeadModule");
                }}
                sx={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `
              0 10px 20px rgba(255, 152, 0, 0.15),
              inset 0 0 0 1px rgba(255, 152, 0, 0.1),
              inset 0 0 20px rgba(255, 152, 0, 0.05)
            `,
                    "& .stat-card-content": {
                      background: "linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 253, 249, 0.98))",
                    }
                  }
                }}
              />
            </motion.div>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <motion.div variants={itemVariants} whileHover={{ scale: 1.02 }}>
              <StatCard
                title="Expired Policies"
                value={formatNumber(expiredUsers)}
                icon={<EventBusy sx={{ color: "#f44336", fontSize: 28 }} />}
                color="#f44336"
                subtitle="Require renewal"
                onClick={() => {
                  setStatusFilter("expired");
                  setActiveSection("LeadModule");
                }}
                sx={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `
              0 10px 20px rgba(244, 67, 54, 0.15),
              inset 0 0 0 1px rgba(244, 67, 54, 0.1),
              inset 0 0 20px rgba(244, 67, 54, 0.05)
            `,
                    "& .stat-card-content": {
                      background: "linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 249, 249, 0.98))",
                    }
                  }
                }}
              />
            </motion.div>
          </Grid>
        </Grid>

        {/* Vehicle Statistics & Status Progress */}
        <Grid container spacing={3}>
          {/* Vehicle Statistics - Left Column */}
          <Grid size={{ xs: 12, md: 8 }}>
            <motion.div variants={itemVariants}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: "100%",
                  mx: { xs: 0, sm: 2 },
                  p: 1,
                  mt: { xs: 2, md: 7 },
                  background: "#111111",
                  border: "1px solid rgba(253, 184, 19, 0.1)",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.5)"
                }}
              >
                <CardContent sx={{ height: "auto", minHeight: { xs: "auto", md: 345 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: { xs: 3, md: 5 } }}>
                    <PieChart sx={{ color: "#FDB813" }} />
                    <Typography variant="h6" fontWeight={800} sx={{ color: "#ffffff", fontFamily: "'Outfit', sans-serif" }}>
                      Vehicle Distribution
                    </Typography>
                    <Chip
                      label={`${formatNumber(totalVehicles)} total`}
                      size="small"
                      sx={{ ml: "auto", backgroundColor: "#667eea10", color: "#667eea" }}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    {vehicleTypeData.filter(v => v.count > 0).length > 0
                      ? vehicleTypeData.filter(v => v.count > 0).map((v, idx) => (
                          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
                            <VehicleTypeCard
                              type={v.type}
                              count={v.count}
                              percentage={totalVehicles > 0 ? Math.round((v.count / totalVehicles) * 100) : 0}
                              color={v.color}
                              icon={v.icon}
                            />
                          </Grid>
                        ))
                      : vehicleTypeData.slice(0, 6).map((v, idx) => (
                          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
                            <VehicleTypeCard
                              type={v.type}
                              count={v.count}
                              percentage={0}
                              color={v.color}
                              icon={v.icon}
                            />
                          </Grid>
                        ))
                    }
                  </Grid>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Policy Status Progress - Right Column */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ mt: { xs: 2, md: 7 }, display: "flex" }}>
            <motion.div variants={itemVariants} style={{ width: "100%", display: "flex" }}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: "100%",
                  flex: 1,
                  width: "100%",
                  background: "#111111",
                  border: "1px solid rgba(253, 184, 19, 0.1)",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
                  mx: { xs: 0, sm: 0 }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={800} mb={3} sx={{ color: "#ffffff", fontFamily: "'Outfit', sans-serif" }}>
                    Policy Status Breakdown
                  </Typography>

                  <StatusProgressCard
                    status="Active"
                    count={activeUsers}
                    total={totalUsers}
                    color="#4caf50"
                  />

                  <StatusProgressCard
                    status="Expiring Soon"
                    count={expiringSoonUsers}
                    total={totalUsers}
                    color="#ff9800"
                  />

                  <StatusProgressCard
                    status="Expired"
                    count={expiredUsers}
                    total={totalUsers}
                    color="#f44336"
                  />

                  <Divider sx={{ my: 3 }} />

                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h5" fontWeight={800} color="#4caf50" fontFamily={"Poppins, sans-serif"}>
                        {totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 100) : 0}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Active Rate
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h5" fontWeight={800} color="#f44336" fontFamily={"Poppins, sans-serif"}>
                        {totalUsers > 0 ? Math.round((expiredUsers / totalUsers) * 100) : 0}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Expired Rate
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        <Box height={80} />
        {/* Quick Stats at Bottom */}
        <motion.div variants={itemVariants}>
          <Card
            sx={{
              mt: { xs: 4, md: 10 },
              borderRadius: 3,
              background: "linear-gradient(135deg, #667eea08 0%, #764ba208 100%)",
              border: "1px solid rgba(102, 126, 234, 0.1)",
              overflow: "hidden",
              mx: { xs: 0, sm: 2 }
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              <Grid
                container
                spacing={3}
                alignItems="center"
                justifyContent="center"
              >
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{
                    textAlign: "center",
                    p: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    <Typography variant="h2" fontWeight={800} color="#667eea" sx={{ fontSize: { xs: "2.5rem", md: "3.75rem" } }} fontFamily={"Poppins, sans-serif"}>
                      {formatNumber(totalVehicles)}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }} fontFamily={"Poppins, sans-serif"}>
                      Total Vehicles
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{
                    textAlign: "center",
                    p: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    <Typography variant="h2" fontWeight={800} color="#4caf50" sx={{ fontSize: { xs: "2.5rem", md: "3.75rem" } }} fontFamily={"Poppins, sans-serif"}>
                      {formatNumber(activeUsers)}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }} fontFamily={"Poppins, sans-serif"}>
                      Active Policies
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{
                    textAlign: "center",
                    p: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    <Typography variant="h2" fontWeight={800} color="#ff9800" sx={{ fontSize: { xs: "2.5rem", md: "3.75rem" } }} fontFamily={"Poppins, sans-serif"}>
                      {formatNumber(expiringSoonUsers)}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }} fontFamily={"Poppins, sans-serif"}>
                      Need Attention
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{
                    textAlign: "center",
                    p: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    <Typography variant="h2" fontWeight={800} color="#f44336" sx={{ fontSize: { xs: "2.5rem", md: "3.75rem" } }} fontFamily={"Poppins, sans-serif"}>
                      {formatNumber(expiredUsers)}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }} fontFamily={"Poppins, sans-serif"}>
                      Require Renewal
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </Box>
  );
}